import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Input, Button, Select, Switch, Space, Spin, message,
  Card, Table, Popconfirm, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getReportDefinition, createReportDefinition, updateReportDefinition,
  type ReportParameterDto,
} from '@/api/dashboardApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const CATEGORIES = ['sales', 'customer', 'activity', 'pipeline'] as const;
const FORMATS = ['table', 'chart', 'csv'] as const;
const PARAM_TYPES = ['string', 'number', 'date', 'boolean', 'select'] as const;

interface ParamRow {
  key: string;
  param_key: string;
  label: string;
  type: string;
  default_value: string;
  required: boolean;
}

export default function ReportForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useState<ParamRow[]>([]);
  const [nextKey, setNextKey] = useState(1);

  const canWrite = hasPermission('reports', 'write');

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getReportDefinition(id)
      .then((res) => {
        if (res.data) {
          const r = res.data;
          form.setFieldsValue({
            name: r.name,
            description: r.description || undefined,
            query_template: r.query_template,
            output_format: r.output_format || 'table',
            category: r.category || undefined,
          });
          setParams(
            (r.parameters ?? []).map((p, i) => ({
              key: `existing_${i}`,
              param_key: p.key,
              label: p.label,
              type: p.type,
              default_value: p.default_value || '',
              required: p.required,
            }))
          );
          setNextKey((r.parameters?.length ?? 0) + 1);
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, form]);

  const addParam = useCallback(() => {
    setParams((prev) => [
      ...prev,
      { key: `new_${nextKey}`, param_key: '', label: '', type: 'string', default_value: '', required: false },
    ]);
    setNextKey((n) => n + 1);
  }, [nextKey]);

  const updateParam = useCallback(
    (key: string, field: keyof ParamRow, value: unknown) => {
      setParams((prev) => prev.map((p) => (p.key === key ? { ...p, [field]: value } : p)));
    },
    []
  );

  const removeParam = useCallback(
    (key: string) => setParams((prev) => prev.filter((p) => p.key !== key)),
    []
  );

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setSaving(true);
      try {
        const paramDtos: ReportParameterDto[] = params.map((p) => ({
          key: p.param_key,
          label: p.label,
          type: p.type,
          default_value: p.default_value || undefined,
          required: p.required,
        }));

        const payload = {
          name: values.name as string,
          description: (values.description as string) || undefined,
          query_template: values.query_template as string,
          output_format: (values.output_format as string) || 'table',
          category: (values.category as string) || undefined,
          parameters: paramDtos,
        };

        if (isEdit) {
          await updateReportDefinition(id, payload);
          message.success(t('reports.update_success'));
          navigate(`/reports/${id}`);
        } else {
          const res = await createReportDefinition(payload);
          message.success(t('reports.create_success'));
          navigate(`/reports/${res.data?.id}`);
        }
      } catch (err) {
        message.error(err instanceof Error ? err.message : t('app.error'));
      } finally {
        setSaving(false);
      }
    },
    [isEdit, id, params, navigate, t]
  );

  useEffect(() => {
    if (!canWrite) navigate('/reports');
  }, [canWrite, navigate]);

  if (!canWrite) return null;
  if (loading) return <Spin size="large" />;

  const paramColumns: ColumnsType<ParamRow> = [
    {
      title: t('reports.parameter.key'),
      dataIndex: 'param_key',
      key: 'param_key',
      width: 120,
      render: (val: string, record: ParamRow) => (
        <Input
          value={val}
          onChange={(e) => updateParam(record.key, 'param_key', e.target.value)}
          placeholder={t('reports.parameter.key')}
        />
      ),
    },
    {
      title: t('reports.parameter.label'),
      dataIndex: 'label',
      key: 'label',
      render: (val: string, record: ParamRow) => (
        <Input
          value={val}
          onChange={(e) => updateParam(record.key, 'label', e.target.value)}
          placeholder={t('reports.parameter.label')}
        />
      ),
    },
    {
      title: t('reports.parameter.type'),
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (val: string, record: ParamRow) => (
        <Select
          value={val}
          style={{ width: '100%' }}
          onChange={(v) => updateParam(record.key, 'type', v)}
          options={PARAM_TYPES.map((pt) => ({ value: pt, label: pt }))}
        />
      ),
    },
    {
      title: t('reports.parameter.default_value'),
      dataIndex: 'default_value',
      key: 'default_value',
      width: 120,
      render: (val: string, record: ParamRow) => (
        <Input
          value={val}
          onChange={(e) => updateParam(record.key, 'default_value', e.target.value)}
        />
      ),
    },
    {
      title: t('reports.parameter.required'),
      dataIndex: 'required',
      key: 'required',
      width: 80,
      align: 'center',
      render: (val: boolean, record: ParamRow) => (
        <Switch
          checked={val}
          size="small"
          onChange={(v) => updateParam(record.key, 'required', v)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, record: ParamRow) => (
        <Popconfirm
          title={t('app.confirm')}
          onConfirm={() => removeParam(record.key)}
          okText={t('app.yes')}
          cancelText={t('app.no')}
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>
        {t(isEdit ? 'reports.edit_title' : 'reports.create_title')}
      </Typography.Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ output_format: 'table' }}
      >
        <Form.Item
          name="name"
          label={t('reports.field.name')}
          rules={[{ required: true, message: t('reports.validation_name_required') }]}
        >
          <Input maxLength={200} />
        </Form.Item>

        <Form.Item name="description" label={t('reports.field.description')}>
          <TextArea rows={2} maxLength={2000} />
        </Form.Item>

        <Space size="large">
          <Form.Item name="category" label={t('reports.field.category')}>
            <Select
              style={{ width: 160 }}
              allowClear
              placeholder={t('reports.field.category')}
              options={CATEGORIES.map((c) => ({
                value: c,
                label: t(`reports.category.${c}`),
              }))}
            />
          </Form.Item>
          <Form.Item name="output_format" label={t('reports.field.output_format')}>
            <Select
              style={{ width: 140 }}
              options={FORMATS.map((f) => ({
                value: f,
                label: t(`reports.format.${f}`),
              }))}
            />
          </Form.Item>
        </Space>

        <Form.Item
          name="query_template"
          label={t('reports.field.query_template')}
          rules={[{ required: true, message: t('reports.validation_query_required') }]}
        >
          <TextArea rows={6} style={{ fontFamily: 'monospace', fontSize: 12 }} />
        </Form.Item>

        <Card
          title={t('reports.field.parameters')}
          extra={
            <Button type="dashed" icon={<PlusOutlined />} onClick={addParam}>
              {t('reports.add_parameter')}
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          <Table<ParamRow>
            dataSource={params}
            columns={paramColumns}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </Card>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(isEdit ? `/reports/${id}` : '/reports')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
